import { ILeaf, INode } from 'shared/types/tree'
import { makeKiddomPostRequest, makeKiddomPutRequest } from 'infrastructure/api'
import { useCallback } from 'react'
import {
  useWithErrorNotification,
  useWithErrorNotificationStatus,
} from 'shared/ui/GlobalDialog'
import { NodeBodyKeys } from 'shared/types/ingestion'
import { normalizeNodes } from 'shared/utils/tree'

type TreeIds = {
  tree_id: string
  version: string
  id: string
}

export function retrieveIds(nodeOrLeaf: TreeIds) {
  return {
    id: nodeOrLeaf.id,
    tree_id: nodeOrLeaf.tree_id,
    version: nodeOrLeaf.version,
  }
}

type NodeRequestData = Omit<Partial<INode>, 'leaves' | 'nodes'>

export type NodeRequestParams = NodeRequestData &
  TreeIds & {
    next_sibling_node_id?: string
    leaves?: Partial<ILeaf>[]
    nodes?: NodeRequestData[]
  }
export type LeafRequestParams = Partial<ILeaf> & TreeIds
export type LeafCreateParams = { leaves: Partial<ILeaf>[] } & TreeIds

function updateNodeRoute({ tree_id, version, id }: TreeIds) {
  return `/api/tree/${tree_id}:${version}/${id}`
}

function updateLeafRoute({ tree_id, version, id }: TreeIds) {
  return `/api/tree/${tree_id}:${version}/leaf/${id}`
}

function createLeafRoute({ tree_id, version, id }: TreeIds) {
  return `/api/tree/${tree_id}:${version}/node/${id}/leaf`
}

function appendRoute({ tree_id, version, id }: TreeIds) {
  return `/api/tree/append/${tree_id}:${version}/${id}`
}

function appendBulkRoute({ tree_id, version, id }: TreeIds) {
  return `/v1/tree/append/${tree_id}:${version}/${id}/bulk`
}

export function updateTreeNode({
  tree_id,
  version,
  id,
  ...rest
}: NodeRequestParams) {
  return makeKiddomPutRequest({
    kiddomPath: updateNodeRoute({ tree_id, version, id }),
    requestBody: rest,
  })
}

export function useUpdateTreeNode({
  showSuccess,
}: { showSuccess?: boolean } = {}) {
  const request = useWithErrorNotificationStatus(
    {
      errorTitle: 'Error Updating Node',
      successMessage: showSuccess ? 'Node has been updated' : undefined,
    },
    updateTreeNode,
  )
  return useCallback(
    (p: NodeRequestParams) => {
      return request(p)
    },
    [request],
  )
}

export function createLeaf({
  tree_id,
  version,
  id,
  ...rest
}: LeafCreateParams) {
  return makeKiddomPostRequest({
    kiddomPath: createLeafRoute({ tree_id, version, id }),
    requestBody: rest,
  })
}

export function updateLeafData({
  tree_id,
  version,
  id,
  ...rest
}: LeafRequestParams) {
  return makeKiddomPutRequest({
    kiddomPath: updateLeafRoute({ tree_id, version, id }),
    requestBody: rest,
  })
}

export async function appendNodeToTree({
  tree_id,
  version,
  id,
  ...rest
}: NodeRequestParams) {
  const doBulkAppend = 'trees' in rest
  const routeFunc = doBulkAppend ? appendBulkRoute : appendRoute

  const { result, error } = await makeKiddomPutRequest<INode>({
    kiddomPath: routeFunc({ tree_id, version, id }),
    requestBody: {
      ...rest,
    },
  })
  return {
    error,
    result: result && normalizeNodes(result),
  }
}

export function useUpdateStudentFacing() {
  const request = useUpdateTreeNode()
  return useCallback(
    async (studentFacing: boolean, ...nodes: INode[]) => {
      return Promise.all(
        nodes.map(async (node) => {
          const params = {
            ...retrieveIds(node),
            body: {
              ...node.body,
              [NodeBodyKeys.StudentFacing]: studentFacing,
            },
          }
          const succeeded = await request(params)
          if (succeeded) {
            if (!node.body) {
              node.body = {}
            }
            node.body[NodeBodyKeys.StudentFacing] = studentFacing
          }
        }),
      )
    },
    [request],
  )
}
